import { useState, useEffect, useCallback, useReducer } from 'react';

import { Stack } from 'ui/stack';

import TabHeader from 'components/partials/tab-header';
import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import { Checkbox } from 'ui/checkbox';
import { Loader } from 'ui/loader';

import { getFilesTableSettings } from 'containers/admin/clients/client/tabs-components/files-tab/files-tab-table-settings';

import { useFilesList } from 'hooks/api/use-files';
import { useCurrentUser } from 'hooks/api/use-current-user';

import { usersReducer } from 'containers/admin/reducer';
import {
  SEARCH_CHANGE,
  FILTER_CHANGE,
  PAGE_CHANGE,
} from 'containers/admin/actions';

import Icons from 'assets/icons';

import api from 'api';

import type { SortingState } from '@tanstack/react-table';
import type { FileRow } from 'containers/admin/clients/client/tabs-components/files-tab/files-tab-table-settings';

interface FilesTabProps {
  clientId: string | number | undefined;
}

interface FilesReducerState {
  search: string;
  pageIndex: number;
}

const initialState: FilesReducerState = {
  search: '',
  pageIndex: 0,
};

interface TableAmountState {
  pageSize: number;
  totalRecords: number;
}

const FilesTab = ({ clientId }: FilesTabProps): React.JSX.Element => {
  const [countArchivedFile, setCountArchivedFile] = useState<
    number | undefined
  >();

  const [state, dispatch] = useReducer(usersReducer, initialState) as [
    FilesReducerState,
    React.Dispatch<{ type: string; payload?: unknown }>
  ];
  const [statusFilter, setStatusFilter] = useState<string[]>([
    'ACTIVE',
    'HIDDEN',
  ]);

  const [{ pageSize }, setAmount] = useState<TableAmountState>({
    pageSize: 10,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'createdDate', desc: false },
  ]);

  const handleSearch = useCallback(
    (payload: string) => dispatch({ type: SEARCH_CHANGE, payload }),
    []
  );

  const handleFilter = useCallback((value: string[]) => {
    dispatch({ type: FILTER_CHANGE });
    setStatusFilter(value);
  }, []);

  const handlePagination = useCallback(
    (payload: number) => dispatch({ type: PAGE_CHANGE, payload }),
    []
  );

  const currentUser = useCurrentUser();
  const currentUserData = currentUser?.data as
    | { oktaUserId?: string; fdAnalyst?: boolean }
    | null
    | undefined;

  const columns = getFilesTableSettings(clientId);

  const { search, pageIndex } = state;

  const {
    data: filesResponse,
    isFetching,
    isError,
    isSuccess,
  } = useFilesList({
    pageSize,
    pageIndex,
    sortBy: sortingParams,
    filters: {
      clientId,
      status: statusFilter,
      search: { value: search },
    },
  });

  const data = (filesResponse?.data ?? []) as FileRow[];
  const totalRecords = filesResponse?.pagination?.totalRecords ?? 0;

  const applyStatusFilter = (): void => {
    const statuses = ['ACTIVE', 'HIDDEN'];

    if (statusFilter.length < 3) {
      statuses.push('ARCHIVED');
    }

    handleFilter(statuses);
  };

  useEffect(() => {
    api
      .getCheckArchivedFile({
        userId: currentUserData?.oktaUserId ?? '',
        clientId: clientId ?? '',
        search,
      })
      .then((response) => {
        setCountArchivedFile(response.data);
      })
      .catch((error) =>
        console.error('Error during fetching file.FileDetails', error)
      );
  }, [search, clientId, currentUserData?.oktaUserId]);

  return (
    <Stack sx={{ position: 'relative', flex: '1 1 0%' }}>
      <Loader
        loading={isFetching && !isSuccess}
        backdrop="white"
        fullScreen={false}
        overlay="transparent"
      />
      <TabHeader
        title="Uploaded files"
        search={search}
        onSearchChange={handleSearch}
        actionButton={
          !currentUserData?.fdAnalyst
            ? {
                label: 'Upload new file',
                to: `/admin/clients/${clientId}/new-file`,
                icon: <Icons.Upload aria-label="upload" />,
                testId: 'upload-file-button',
              }
            : undefined
        }
      />

      <Stack sx={{ position: 'relative', flex: '1 1 0%' }}>
        {!!countArchivedFile && (
          <Checkbox
            label="Show archived files"
            id="statusFilter"
            value="archived"
            checked={statusFilter.length > 2}
            onChange={applyStatusFilter}
            size="small"
            sx={{ position: 'absolute', py: 0.75 }}
            data-testid="checkbox-statusFilter"
          />
        )}

        {(!isError && !!data.length) || (isFetching && isSuccess) ? (
          <TableComponent
            columns={columns}
            data={data}
            isLoading={isFetching}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPagination={handlePagination}
            setAmount={setAmount}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            totalRecords={totalRecords}
            customHeight="max-h-[50vh]"
          />
        ) : !isFetching && !data.length ? (
          <EmptySpaceContent
            customClass="flex-1"
            text="There are no files to show yet."
            linkShow={!!countArchivedFile}
            linkText="Show archived files"
            onClick={applyStatusFilter}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};

export default FilesTab;
